import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpenseReportsBox, {
  ExpenseReportsAnalyticsEventProperties,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import {
  UserDropdownInfiniteScrollProps,
  userIdOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { UserListModel } from 'src/models/user.models'
import { expenseReportsQueries } from 'src/queries/expenseReports/expenseReports.queries'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import {
  createSetIsDeleteExpenseReportsPopupOpen,
  createSetIsMarkExpenseReportsPaidPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  selectIsMarkExpenseReportsPaidPopupOpenFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

interface ApproverExpenseReportsProps {
  allExpenses?: boolean
  yourExpenses?: boolean
}

const ApproverExpenseReports: React.FC<ApproverExpenseReportsProps> = (
  props,
) => {
  const dispatch = useBackgroundDispatch()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const outOfPocketExpensesQuery = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const gettingOutOfPocketExpensesEnabled = outOfPocketExpensesQuery.isLoading
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    outOfPocketExpensesQuery.data,
  )
  const submitterUsersQuery = useQuery(expenseReportsQueries.submitterUsers())
  const allMemberIdOptions = useMemo(
    () =>
      (submitterUsersQuery.data ?? []).map((user) =>
        userIdOption(new UserListModel(user)),
      ),
    [submitterUsersQuery.data],
  )
  const isMarkExpenseReportsPaidPopupOpen = useSelector(
    selectIsMarkExpenseReportsPaidPopupOpenFromState,
  )
  const isDeleteExpenseReportPopupOpen = useSelector(
    selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  )
  const [bulkSelected, setBulkSelected] = useState<boolean>(false)
  const [selectedExpenseReports, setSelectedExpenseReports] = useState<
    ExpenseReportModel[]
  >([])
  const user = useCurrentUser()
  const hasApproverPermissions = user?.id === organization?.expenseApprover?.id
  const hasReadPermissions = user?.hasOrgBookkeeperPermissions
  const appName = useSelector(selectAppName)
  const canViewAllReports =
    (hasApproverPermissions || hasReadPermissions) && !!props.allExpenses
  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    ExpenseReportsAnalyticsEventProperties | undefined
  >(undefined)

  const showYourReports =
    (!canViewAllReports || !!props.yourExpenses) && !props.allExpenses

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: () => {
      void submitterUsersQuery.refetch()
    },
    loading: submitterUsersQuery.isFetching,
    complete: !submitterUsersQuery.isPending,
  }

  const handleExpenseReportsCheckboxClick = (
    option: ExpenseReportModel,
    allReports: ExpenseReportModel[],
  ): void => {
    if (!selectedExpenseReports.includes(option)) {
      if (selectedExpenseReports.length + 1 === allReports.length) {
        setBulkSelected(true)
      }
      setSelectedExpenseReports((prev) => [...prev, option])
    } else {
      if (bulkSelected) {
        setBulkSelected(false)
      }
      setSelectedExpenseReports((prev) =>
        prev.filter((item) => item !== option),
      )
    }
  }

  const handleBulkExpenseCheckboxClick = (
    allReports: ExpenseReportModel[],
  ): void => {
    // your reports are deletable as drafts; all reports are markable-as-paid
    // once reviewed
    const selectableStatus = showYourReports
      ? ExpenseReportStatus.Draft
      : ExpenseReportStatus.Reviewed
    bulkSelected
      ? setSelectedExpenseReports([])
      : setSelectedExpenseReports(
          allReports.filter((item) => item.status === selectableStatus),
        )
    setBulkSelected(!bulkSelected)
  }

  const handleReportBulkAction = (): void => {
    if (showYourReports) {
      handleDeleteReports()
    } else {
      handleReportPaidSubmit()
    }
  }

  const handleDeleteReports = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    dispatch(createSetIsDeleteExpenseReportsPopupOpen(true, expenseReportIds))
  }

  const handleReportPaidSubmit = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    const totalAmountApproved = selectedExpenseReports
      .map((expenseReport) => expenseReport.approvedAmountCents)
      .reduce((a, b) => a + b)
    dispatch(
      createSetIsMarkExpenseReportsPaidPopupOpen(
        true,
        expenseReportIds,
        totalAmountApproved,
      ),
    )
  }

  const handleCloseToolbar = (): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }

  useEffect((): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }, [showYourReports])

  useEffect((): void => {
    if (!isMarkExpenseReportsPaidPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isMarkExpenseReportsPaidPopupOpen])

  useEffect((): void => {
    if (!isDeleteExpenseReportPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isDeleteExpenseReportPopupOpen])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track(
        `${showYourReports ? 'your' : 'all'} expense reports filter applied`,
        {
          report_status: selectedFilters.report_status,
          report_submitted_by: selectedFilters.report_submitted_by,
        },
      )
    }
  }, [selectedFilters])

  return gettingOutOfPocketExpensesEnabled ? (
    <LoadingBackgroundPage />
  ) : outOfPocketExpensesEnabled ? (
    <>
      <Helmet>
        <title>{`Reimbursements | ${appName}`}</title>
        <meta
          name="description"
          content={`Reimbursement Reports page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ExpenseReportsBox
          bulkSelected={bulkSelected}
          selectedExpenseReports={selectedExpenseReports}
          handleExpenseReportsCheckboxClick={handleExpenseReportsCheckboxClick}
          handleBulkExpenseReportsCheckboxClick={handleBulkExpenseCheckboxClick}
          handleReportBulkAction={handleReportBulkAction}
          handleCloseToolbar={handleCloseToolbar}
          userOptions={allMemberIdOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          allReports={!!props.allExpenses}
          hasApproverPermissions={hasApproverPermissions}
          setSelectedFilters={setSelectedFilters}
          context={props.allExpenses ? 'approver' : 'submitter'}
        />
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default ApproverExpenseReports
